import { GeoJSON } from 'geojson'
import { isValidPhoneNumber } from 'libphonenumber-js'
import { head, isEqual, last } from 'lodash-es'
import turfRewind from '@turf/rewind'
import { useContext, useEffect, useLayoutEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import { toaster } from '@/components/Toaster'
import { SearchItem } from '@/feature/map/hooks/useAutoCompleteLocationSearch'
import {
  GeoFenceCreateSchema,
  GeoFenceCreateSchemaType,
  GeoMode,
  ObservatoryContext,
  useObservatoryPageStore,
} from '@/feature/observatory'
import { usePersistentObservatoryPageStore } from '@/feature/observatory/store/usePersistentObservatoryPageStore'
import { GeoFence, GeofenceCreateUpdatePayload } from '@/feature/observatory/types/GeoFence.types'
import { captureError } from '@/utils/captureError'

import { MapMessage } from './components/MapMessage'
import { formDefaults, getGeoFencePosition } from './GeoFenceCreateEdit.utils'
import { GeoFenceCreateEditProps } from './GeoFenceCreateEdit.types'

/**
 * Centres the map on the geofence being edited.
 *
 * `searchLocation` is written in a layout effect so it lands before any child effect runs —
 * otherwise `GeoFenceTarget` initialises it from the map centre, and the async reverse
 * geocode that follows overwrites the geofence's real home coordinate. When creating, the
 * value is cleared so `GeoFenceTarget` falls back to the map centre rather than reusing the
 * last-used coordinate.
 */
const usePanGeoFenceIntoView = (geoFence?: GeoFence) => {
  const setSearchLocation = usePersistentObservatoryPageStore((state) => state.setSearchLocation)

  const map = useObservatoryPageStore((state) => state.map)

  useLayoutEffect(() => {
    if (geoFence) {
      const position = getGeoFencePosition(geoFence)
      if (position) {
        setSearchLocation({
          address: geoFence.address,
          location: position,
        })
      }
    } else {
      setSearchLocation(undefined)
    }
  }, [geoFence])

  // `map` belongs in the dependencies: it is created asynchronously, so on the first render
  // the store often still holds nothing and `map?.panTo` silently no-ops. Keyed on
  // [geoFence] alone the effect never retried, and the geofence being edited was left
  // off-screen until the user panned to it by hand.
  useEffect(() => {
    if (geoFence) {
      const position = getGeoFencePosition(geoFence)
      if (position) {
        map?.panTo({ position })
      }
    }
  }, [geoFence, map])
}

/** Hides the asset list and geofence aside for the duration of the edit, and restores both. */
const useSetupPageUI = (closeAction: () => void) => {
  const asides = usePersistentObservatoryPageStore((state) => state.asides)
  const setGeoFencesAside = usePersistentObservatoryPageStore((state) => state.toggles.setGeoFences)

  const setMapMessage = useObservatoryPageStore(({ setMapMessage }) => setMapMessage)

  useEffect(() => {
    const assetsShowing = asides.hideAssets
    asides.setHideAssets(true)
    setGeoFencesAside(false)
    setMapMessage(<MapMessage closeAction={closeAction} />)

    return () => {
      setGeoFencesAside(true)
      asides.setHideAssets(assetsShowing)
      setMapMessage(undefined)
    }
  }, [])
}

/** Builds the create/update payload from the form and dispatches it to the API. */
const useOnSubmitHandle = (
  simulatedPosition: SearchItem,
  geoFence: GeoFence | undefined,
  setSubScreen: (subScreen: string) => void,
) => {
  const { createGeoFence, editGeoFence } = useContext(ObservatoryContext)
  return (f: GeoFenceCreateSchemaType) => {
    const geoJson: GeoJSON =
      f.geoFence.type === GeoMode.linestring
        ? ({
            ...geoFence!.geoJson,
            properties: {
              radius: f.geoFence.radius, // Update the radius property
              ...(f.geoFence.color ? { color: f.geoFence.color } : {}),
            },
          } as GeoJSON) // Preserve existing LineString geometry but update radius
        : f.geoFence.type === GeoMode.circle
          ? {
              type: 'Feature',
              geometry: {
                type: 'Point',
                coordinates: [simulatedPosition.location?.lng, simulatedPosition.location?.lat],
              },
              properties: {
                radius: f.geoFence.radius,
                home: simulatedPosition.location,
              },
            }
          : turfRewind(
              {
                type: 'Feature',
                geometry: {
                  type: 'Polygon',
                  coordinates: [
                    f.geoFence.points
                      .map((p) => [p.lng, p.lat])
                      .concat(
                        isEqual(head(f.geoFence.points), last(f.geoFence.points))
                          ? []
                          : [[f.geoFence.points[0].lng, f.geoFence.points[0].lat]],
                      ),
                  ],
                },
                properties: {
                  home: simulatedPosition.location,
                },
              },
              { mutate: true },
            )

    const geoFencePayload: GeofenceCreateUpdatePayload = {
      title: f.geoFence.title,
      highRiskArea: f.geoFence.highRisk,
      address: f.geoFence.address,
      geoJson,
      alertRules: f.alerts.map((alert) => {
        const { true: sendAlertToPhoneNumbers = [], false: sendAlertToEmails = [] } =
          Object.groupBy(alert.contacts, (contact: string) => String(isValidPhoneNumber(contact)))
        return {
          id: alert.alertId,
          title: alert.title,
          enabledAlert: alert.enabledAlert,
          alertOnEnter: alert.alertOnEnter,
          alertOnExit: alert.alertOnExit,
          dwellTime: alert.dwellTime,
          emails: sendAlertToEmails,
          phoneNumbers: sendAlertToPhoneNumbers,
          tags: alert.tags.map((tag) => tag.id),
        }
      }),
    }

    if (geoFence) {
      editGeoFence(geoFence.id, geoFencePayload)
        .then(() => toaster({ message: 'Successfully Updated GeoFence', type: 'success' }))
        .catch(captureError(() => toaster({ message: 'Error Updating GeoFence', type: 'error' })))
    } else {
      createGeoFence(geoFencePayload)
        .then(() => {
          toaster({ message: 'Successfully Created GeoFence', type: 'success' })
        })
        .catch(captureError(() => toaster({ message: 'Error Creating GeoFence', type: 'error' })))
    }
    setSubScreen('main')
  }
}

/**
 * Owns every piece of state and side effect behind the geofence create/edit aside, so the
 * component itself is only composition.
 */
export const useGeoFenceCreateEdit = ({ setSubScreen, geoFence }: GeoFenceCreateEditProps) => {
  const searchLocation = usePersistentObservatoryPageStore((state) => state.searchLocation)

  const [dialogOpen, setDialogOpen] = useState(false)

  const formMethods = useForm({
    resolver: zodResolver(GeoFenceCreateSchema),
    defaultValues: formDefaults(geoFence),
  })

  const [simulatedPosition, setSimulatedPosition] = useState<SearchItem | undefined>(searchLocation)

  // Ensure simulatedPosition updates for LineString geofences when switching between geofences
  useEffect(() => {
    if (searchLocation) {
      setSimulatedPosition(searchLocation)
    }
  }, [searchLocation])

  usePanGeoFenceIntoView(geoFence)

  const closeAction = () => {
    setDialogOpen(true)
  }

  useSetupPageUI(closeAction)

  const closeScreen = () => {
    setSubScreen('main')
    setDialogOpen(false)
  }

  // Called at the top level rather than inline in the JSX, where it previously sat inside the
  // handleSubmit() argument and so ran as a conditionally-positioned hook.
  const onSubmit = useOnSubmitHandle((simulatedPosition ?? searchLocation)!, geoFence, setSubScreen)

  return {
    formMethods,
    onSubmit,
    dialogOpen,
    setDialogOpen,
    closeAction,
    closeScreen,
    simulatedPosition,
    setSimulatedPosition,
  }
}
